import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Drawer, InputRef, message, Modal, Space, Table, Typography } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { SorterResult } from "antd/es/table/interface";
import { ColumnsType } from "antd/es/table";
import moment from "moment";

import { SCMaxULogsService } from 'services'
import { ScmaxUlogRecord } from "types";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import { GetSCMaxULogsRecordsParams } from "services/scmax-ulogs";
import SCMaxULogsFilters from "../components/SCMaxULogRecordsFilters/"
import { SCMaxULogsFilter } from "../types";
import { objectToUrlParams, urlParamsToObject } from "../../../utils/url-utils";
import { history } from "../../../utils/history";
import { deepCamelCaseKeys } from "../../../utils/objects";
import { getColumnSearchProps } from "../../../components/TableSearch";

// ----- Global variables -----
const DEFAULT_PAGE_SIZE = 100


const ScmaxULogsListPage = () => {
	// ----- Navigation -----
	const [searchParams] = useSearchParams();

	// ----- Data -----
	const [SCMaxULogs, setUserLogs] = useState<ScmaxUlogRecord[]>([])
	const [selectedSCMaxULogId, setSelectedSCMaxULogId] = useState<number | null>(null)
	const [loading, setLoading] = useState<boolean>(false)

	// ----- Filters -----
	const [filters, setFilters] = useState<SCMaxULogsFilter>()

	// ----- Modal -----
	const [isModalOpen, setIsModalOpen] = useState(false);

	// ----- Table -----
	const [totalItems, setTotalItems] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)// todo: add search field

	const searchInput = useRef<InputRef>(null);
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [search,] = useState<string>('')
	const [sortField, setSortField] = useState<string | undefined>()
	const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>()

	// ----- Drawer -----
	// todo: if mac id present in the url no drawer is needed
	const [open, setOpen] = useState(true);

	// ----- Hooks -----
	useEffect(() => {
		// fetch data from server
		fetchData()
	}, [currentPage, search, sortField, sortOrder, filters])

	useEffect(() => {
		// get filters from url
		const filtersInUrl = urlParamsToObject<SCMaxULogsFilter>(searchParams)
		const rids = Array.isArray(filtersInUrl?.rids) ? filtersInUrl?.rids : (filtersInUrl?.rids ? [filtersInUrl?.rids] : [])
		if (filtersInUrl) {
			// Update the filters state
			setFilters(prevFilters => ({
				...prevFilters,
				...filtersInUrl,
				rids,
			}));
		}
		// Run this effect whenever 'searchParams' changes
	}, [searchParams])

	// ----- Drawer -----
	const showDrawer = () => {
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};
	// ----- End Drawer -----

	// ----- Modal -----
	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setSelectedSCMaxULogId(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setSelectedSCMaxULogId(null);
	};
	// ----- End Modal -----

	const fetchData = async () => {
		if (!filters?.macAddress) return
		setLoading(true)
		try {
			const params: GetSCMaxULogsRecordsParams = {
				mac_address: filters?.macAddress || '', // "FDC6E5B9CFD1",
				rids: filters?.rids || [],
				...(filters?.startDate ? { start_date: filters?.startDate } : {}),
				...(filters?.endDate ? { end_date: filters?.endDate } : {}),
				page: currentPage,
				page_size: pageSize,
			}
			const response = await SCMaxULogsService.getSCMaxULogsRecords(params)
			if (!response)
				throw Error("Invalid response")
			const { items, count } = response

			const formattedSCMaxULogs = items?.map((item: ScmaxUlogRecord) => ({
				...deepCamelCaseKeys<ScmaxUlogRecord>(item),
				key: `scmax-ulog-record-${item?.id}`,
			}))
			setUserLogs(formattedSCMaxULogs)
			setTotalItems(count)
		} catch (err) {
			// Show error message
			console.error('Error getting user logs')
			message.error(`Failed to get user-logs`);
		}
		setLoading(false)
	}

	// ----- Handlers -----
	const handleViewRecordDetails = (record: ScmaxUlogRecord) => {
		setSelectedSCMaxULogId(record.id)
		showModal()
	}

	const handleOnFilter = (values: SCMaxULogsFilter) => {
		setFilters(values)
		setOpen(false)
		// add filters to url
		const url = objectToUrlParams(values);
		history?.navigate?.(url);
	}

	const columns: ColumnsType<ScmaxUlogRecord> = [
		{
			title: 'Id',
			dataIndex: 'id',
			key: 'id',
			...getColumnSearchProps({
				searchInput,
				setSearchText,
				setSearchedColumn,
				searchedColumn,
				searchText,
				dataIndex: 'id'
			}),
		},
		{
			title: 'Type',
			dataIndex: 'name',
			key: 'name',
			render: (value) => value?.replace('Record', '')
		},
		{
			title: 'Timestamp',
			dataIndex: 'ts',
			key: 'ts',
			render: (value) => <>{value}</>
		},
		{
			title: 'Info',
			dataIndex: 'parsedJson',
			key: 'parsedJson',
			render: (value) => <>{JSON.stringify(value)}</>
		}, {
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			render: (_: object, record: ScmaxUlogRecord) => {
				return (
					<Button onClick={() => handleViewRecordDetails(record)} type="primary" icon={<EyeOutlined />} />
				)
			}

		}
	]

	const selectedSCMaxUserLog = SCMaxULogs.find(item => item.id === selectedSCMaxULogId) || null
	return (
		<>
			<CustomBreadcrumb list={['SCMAX ULogs', 'List',]} />
			<Card title="SCMAX User logs"
				extra={
					<Space>
						<Button
							loading={loading}
							onClick={() => fetchData()}
							icon={<ReloadOutlined />} />
						<Button type="primary"
							loading={loading}
							onClick={() => showDrawer()}
						>
							Filters
						</Button>
					</Space>
				}
			>
				<Table<ScmaxUlogRecord>
					columns={columns}
					dataSource={SCMaxULogs}
					loading={loading}
					size="small"
					bordered
					pagination={{
						pageSize: pageSize,
						current: currentPage,
						total: totalItems,
						showSizeChanger: true,
						pageSizeOptions: ['10', '20', '30', '50', '100'],
						showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
					}}
					onChange={(pagination, filters, sorter: SorterResult<ScmaxUlogRecord> | SorterResult<ScmaxUlogRecord>[],) => {
						// setFilters(filters)
						setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
						setCurrentPage(pagination.current || 1);
						console.log('pagination.current ', pagination.current);
						if (typeof sorter === 'object') {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							setSortField(sorter?.field?.toString())
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							setSortOrder(sorter?.order?.toString())
						}
					}}
				/>
			</Card>
			<Drawer
				title="Filter"
				placement="left"
				closable={false}
				onClose={onClose}
				open={open}
				key={"DrawerFilter"}
			>
				<SCMaxULogsFilters onSubmit={handleOnFilter} filters={filters} loading={loading} />
			</Drawer>
			<Modal title="JSON record data" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
				<pre>{selectedSCMaxUserLog && JSON.stringify(selectedSCMaxUserLog, null, 2)}</pre>
			</Modal>
		</>
	)
}

export default ScmaxULogsListPage
